import { useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { SupportRequestFormData } from '../schemas/supportRequestFormSchema';
import { PaymentStartTiming } from '@/models/common/PaymentStartTiming';
import { BankAccountDtoIn } from '@/models/in/BankAccountDtoIn';
import useFundContext from '@/contexts/Fund/useFundContext';
import InfoPanel, { InfoPanelItem } from '@/components/ui/InfoPanel/InfoPanel';
import Checkbox from '@/components/ui/Checkbox/Checkbox';
import warningIcon from '@/assets/icons/info2.svg';
import calendarIcon from '@/assets/icons/calendar.svg';
import { useGetAuthApi } from '@/hooks/UseApiCalls';
import { formatDateLocale } from '@/helpers/dateHelper';
import { buildAvailableBalancePendingTooltipText } from '@/helpers/availableBalanceTooltipText';
import { EligibleAmountBreakdownDtoIn } from '@/models/in/EligibleAmountBreakdownDtoIn';
import ThirdPartyPaymentSection from './ThirdPartyPaymentSection';
import AddBankAccountInline from './AddBankAccountInline';
import SupportAmountInput from '../SupportAmountInput';
import UpcomingStandingSupportsNotice from '../UpcomingStandingSupportsNotice';
import wizardStyles from '@/pages/SupportRequests/NewSupportRequest.module.scss';

interface PaymentSetupProps {
  paymentInfos: BankAccountDtoIn[];
}

const formatCurrency = (value: number) => new Intl.NumberFormat('he-IL').format(value) + ' ₪';

const getDayOfMonth = (isoDate: string) => {
  const date = new Date(isoDate);
  return date.getDate();
};

const PaymentSetup = ({ paymentInfos }: PaymentSetupProps) => {
  const { t } = useTranslation();
  const { currentFund, balance } = useFundContext();
  const queryClient = useQueryClient();
  const { watch, setValue } = useFormContext<SupportRequestFormData>();
  const multiRequestTotalCount = watch('multiRequestTotalCount');
  const paymentStartTiming = watch('paymentStartTiming');
  const paymentStartMonth = watch('paymentStartMonth');
  const transferDayOfMonth = watch('transferDayOfMonth');
  const paymentInfoId = watch('paymentInfoId');
  const amount = watch('amount');
  const goalWithLimits = watch('goalWithLimits');
  const exceptionsCommitteeAcknowledged = watch('exceptionsCommitteeAcknowledged');
  const exceedsBalanceComment = watch('exceedsBalanceComment');
  const supported = watch('supported');
  const childId = watch('childId');

  /*
   * SPR-10282: accounts added from this step, kept here until the supported's additional-info
   * query has refetched - so the new account is in the list (and selected) the moment it is saved,
   * not a round trip later. Dropped from the merge once the refetch carries it.
   */
  const [addedAccounts, setAddedAccounts] = useState<BankAccountDtoIn[]>([]);
  const accountOptions = useMemo(
    () => [...paymentInfos, ...addedAccounts.filter((added) => !paymentInfos.some((existing) => existing.id === added.id))],
    [paymentInfos, addedAccounts]
  );

  const handleAccountAdded = async (account: BankAccountDtoIn) => {
    setAddedAccounts((current) => [...current, account]);
    setValue('paymentInfoId', account.id, { shouldValidate: true, shouldDirty: true });
    if (currentFund?.id && supported?.id) {
      // Same refresh the «פרטי תשלום» window does, so the supporteds screen sees the account too.
      await queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey[0];
          return typeof key === 'string' && key.includes(`supporteds/${supported.id}/additional-info`);
        },
      });
    }
  };

  // Calculate first payment date from backend
  const firstPaymentDateUrl = useMemo(() => {
    const totalCount = multiRequestTotalCount || 1;
    let url = `calculate-payment-date?monthCounter=1&transferDayOfMonth=${transferDayOfMonth}&multiRequestTotalCount=${totalCount}&paymentStartTiming=${paymentStartTiming}`;
    if (paymentStartTiming === PaymentStartTiming.SpecificMonth && paymentStartMonth) {
      url += `&paymentStartMonth=${paymentStartMonth}`;
    }
    return url;
  }, [multiRequestTotalCount, paymentStartTiming, paymentStartMonth, transferDayOfMonth]);

  const { data: requestedForDate, isError: isRequestedForDateError } = useGetAuthApi<string>(firstPaymentDateUrl);

  // Use calculated date for eligible amount (extract date part from ISO datetime)
  const eligibleAmountUrl = useMemo(() => {
    if (!currentFund?.id || !supported?.id || !goalWithLimits?.goal?.id || !requestedForDate) return '';
    const dateOnly = requestedForDate.split('T')[0];
    let url = `funds/${currentFund.id}/supporteds/${supported.id}/eligible-amount-breakdown?goalTypeId=${goalWithLimits.goal.id}&requestedForDate=${dateOnly}`;
    if (childId) {
      url += `&requestedBecauseOfContactId=${childId}`;
    }
    if (multiRequestTotalCount && multiRequestTotalCount > 1) {
      url += `&isMultipleMonths=true`;
    }
    return url;
  }, [currentFund?.id, supported?.id, goalWithLimits?.goal?.id, requestedForDate, childId, multiRequestTotalCount]);

  // The eligibility arrives split into its regular part and the part a holiday period adds on top, so
  // each can be presented on its own row. The sum is what the requested amount is validated against.
  const { data: eligibility, isError: isEligibilityError } = useGetAuthApi<EligibleAmountBreakdownDtoIn>(eligibleAmountUrl);
  const eligibleAmount = eligibility?.eligibleAmount;
  // HAT-1369: a failed call is not a calculation still running. Showing "מחשב..." for it hid
  // that the request was going out with no eligibility check at all.
  const isEligibilityUnavailable = isRequestedForDateError || isEligibilityError;

  const goalEnablesMultipleMonths = goalWithLimits?.goal?.name === 'MonthlySupport' || goalWithLimits?.goal?.name === 'Kollel';
  const isMultipleMonths = multiRequestTotalCount !== null && multiRequestTotalCount > 1;
  const isAfter25thOfMonth = new Date().getDate() > 25;
  const availableBalance = balance?.calculateAvailableBalance() ?? 0;
  // HAT-1396: each payment costs the fund its support-row fee on top of the amount (server checks the same).
  const supportRowFee = balance?.supportRequestsBalances?.supportRowFee ?? 0;
  // Skip balance check for monthly requests (multiple months) - current balance is not relevant for future monthly payments
  const exceedsAvailableBalance = !isMultipleMonths && amount > 0 && amount + supportRowFee > availableBalance;
  const exceedsEligibleAmount = amount > 0 && eligibleAmount !== undefined && eligibleAmount !== Number.MAX_VALUE && amount > eligibleAmount;

  // Sync exceedsAvailableBalance and exceedsEligibleAmount state with form
  useEffect(() => {
    setValue('exceedsAvailableBalance', exceedsAvailableBalance);
    setValue('exceedsEligibleAmount', exceedsEligibleAmount);
    // Reset acknowledgement and comment when no longer exceeding balance or eligible amount
    if (!exceedsAvailableBalance && !exceedsEligibleAmount) {
      setValue('exceptionsCommitteeAcknowledged', false);
      setValue('exceedsBalanceComment', '');
    }
  }, [exceedsAvailableBalance, exceedsEligibleAmount, setValue]);

  // Reset date-related values when not selecting multiple months
  useEffect(() => {
    if (!isMultipleMonths) {
      setValue('paymentStartTiming', PaymentStartTiming.Immediate);
      setValue('paymentStartMonth', null);
      setValue('transferDayOfMonth', 1);
    }
  }, [isMultipleMonths, setValue]);

  // Reset paymentStartMonth when switching away from SpecificMonth
  useEffect(() => {
    if (paymentStartTiming !== PaymentStartTiming.SpecificMonth) {
      setValue('paymentStartMonth', null);
    }
  }, [paymentStartTiming, setValue]);

  // Reset to NextTransferDate if Immediate is selected after the 25th of the month
  useEffect(() => {
    if (isAfter25thOfMonth && paymentStartTiming === PaymentStartTiming.Immediate) {
      setValue('paymentStartTiming', PaymentStartTiming.NextTransferDate);
    }
  }, [isAfter25thOfMonth, paymentStartTiming, setValue]);

  // Generate month options for the month selector
  // Show 11 months starting from next month (excludes current month to avoid backend misinterpretation)
  const futureMonthOptions = useMemo(() => {
    const months = [];
    const today = new Date();
    for (let i = 1; i <= 11; i++) {
      const date = new Date(today.getFullYear(), today.getMonth() + i, 1);
      const monthNumber = date.getMonth() + 1; // 1-12
      const monthName = date.toLocaleDateString('he-IL', { month: 'long' });
      const label = `${monthNumber.toString().padStart(2, '0')} - ${monthName}`;
      months.push({ value: monthNumber.toString(), label });
    }
    return months;
  }, []);

  const monthOptions = Array.from({ length: 12 }, (_, i) => i + 1);

  const getEligibleAmountDisplay = (amount: number | undefined) => {
    // Anything that is not a number (a response shape this build does not expect, a missing field)
    // must not reach formatCurrency - it would render as "NaN ₪" to the coordinator.
    if (typeof amount !== 'number' || Number.isNaN(amount)) {
      return isEligibilityUnavailable ? t('eligibleAmountUnavailable') : t('calculating');
    }
    if (amount === Number.MAX_VALUE) return '—';
    return formatCurrency(amount);
  };

  // Generate support date display text based on user selections
  const getSupportDateDisplay = () => {
    if (!requestedForDate) return null;

    // Case 1: Single payment (not multi-month goal or just 1 month)
    if (!goalEnablesMultipleMonths || !multiRequestTotalCount || multiRequestTotalCount === 1) {
      return {
        type: 'single',
      };
    }

    // Case 2: Multiple months - following payments are always on transferDayOfMonth
    const firstPaymentDay = getDayOfMonth(requestedForDate);
    const isFirstPaymentOnDifferentDay = firstPaymentDay !== transferDayOfMonth;

    if (isFirstPaymentOnDifferentDay) {
      // First date differs from following dates (Immediate timing)
      return {
        type: 'mixed',
        count: multiRequestTotalCount,
        firstDate: formatDateLocale(requestedForDate),
        followingDay: transferDayOfMonth,
      };
    } else {
      // All dates are on the same day of month
      return {
        type: 'uniform',
        count: multiRequestTotalCount,
        day: transferDayOfMonth,
        startDate: formatDateLocale(requestedForDate),
      };
    }
  };

  const supportDateInfo = getSupportDateDisplay();
  const [showSupportDates, setShowSupportDates] = useState(false);

  const fundBalanceItems: InfoPanelItem[] = [
    {
      label: t('fundBalance'),
      value: balance ? formatCurrency(balance.balancesByKind?.total ?? 0) : '—',
    },
    {
      label: t('availableBalance'),
      value: balance ? formatCurrency(balance.calculateAvailableBalance()) : '—',
      hasInfoIcon: true,
      infoTooltip: buildAvailableBalancePendingTooltipText(t, formatCurrency, balance?.supportRequestsBalances),
    },
    {
      label: t('amountLimitByClassification'),
      value: getEligibleAmountDisplay(eligibility?.regularEligibleAmount),
    },
    // The holiday row appears only when this request actually draws extra eligibility from a holiday
    // period, and it carries that period's name as its label.
    ...(eligibility?.holidayPeriodName
      ? [
          {
            label: eligibility.holidayPeriodName,
            value: getEligibleAmountDisplay(eligibility.holidayEligibleAmount),
          },
        ]
      : []),
  ];

  return (
    <div className={`w-full max-w-[1132px] px-4 flex flex-row gap-6 ${wizardStyles.paymentSetupRow}`}>
      {/* Main Form Section */}
      <div className="flex-1">
        <div className="flex items-start justify-between">
          <h2 className="body-lr-bold text-(--font-sub-title-2)">{t('paymentSetup')}</h2>
        </div>

        {goalEnablesMultipleMonths && (
          <div className="flex flex-row max-md:flex-col gap-6 max-md:gap-4 mt-10 max-md:mt-6 flex-wrap">
            <div className="flex-1">
              <label className="body-sm-regular text-(--font-heading) block mb-1.5">{t('numberOfMonthlyPayments')}</label>
              <select
                className="body-md-regular text-(--font-body-1) p-3 border border-(--border-main) rounded-lg w-full"
                value={multiRequestTotalCount ?? ''}
                onChange={(e) => setValue('multiRequestTotalCount', Number(e.target.value))}
              >
                <option value="" disabled>
                  {t('selectNumberOfMonths')}
                </option>
                {monthOptions.map((num) => (
                  <option key={num} value={num}>
                    {num} {t('months')}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex-1">
              <label className="body-sm-regular text-(--font-heading) block mb-1.5">{t('paymentStartTiming')}</label>
              <select
                className={`body-md-regular text-(--font-body-1) p-3 border border-(--border-main) rounded-lg w-full ${!isMultipleMonths ? 'opacity-50 cursor-not-allowed' : ''}`}
                value={paymentStartTiming}
                onChange={(e) => setValue('paymentStartTiming', e.target.value as PaymentStartTiming)}
                disabled={!isMultipleMonths}
              >
                <option value={PaymentStartTiming.Immediate} disabled={isAfter25thOfMonth}>
                  {t('immediate')}
                  {isAfter25thOfMonth && ` - ${t('immediateDisabledAfter25th')}`}
                </option>
                <option value={PaymentStartTiming.NextTransferDate}>{t('nextTransferDate')}</option>
                <option value={PaymentStartTiming.SpecificMonth}>{t('specificMonth')}</option>
              </select>
            </div>

            {paymentStartTiming === PaymentStartTiming.SpecificMonth && (
              <div className="flex-1">
                <label className="body-sm-regular text-(--font-heading) block mb-1.5">{t('selectStartMonth')}</label>
                <select
                  className="body-md-regular text-(--font-body-1) p-3 border border-(--border-main) rounded-lg w-full"
                  value={paymentStartMonth ?? ''}
                  onChange={(e) => setValue('paymentStartMonth', e.target.value)}
                >
                  <option value="" disabled>
                    {t('selectMonth')}
                  </option>
                  {futureMonthOptions.map((month) => (
                    <option key={month.value} value={month.value}>
                      {month.label}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {isMultipleMonths && (
              <div className="flex-1">
                <label className="body-sm-regular text-(--font-heading) block mb-1.5">{t('transferDayOfMonth')}</label>
                <select
                  className="body-md-regular text-(--font-body-1) p-3 border border-(--border-main) rounded-lg w-full"
                  value={transferDayOfMonth}
                  onChange={(e) => setValue('transferDayOfMonth', Number(e.target.value) as 1 | 15)}
                >
                  <option value={1}>1 {t('ofMonth')}</option>
                  <option value={15}>15 {t('ofMonth')}</option>
                </select>
              </div>
            )}
          </div>
        )}

        {/* Support Date Display */}
        <div className="mt-4">
          <button
            type="button"
            onClick={() => setShowSupportDates(!showSupportDates)}
            className="flex items-center gap-2 body-md-regular text-(--font-link) hover:underline cursor-pointer"
          >
            <img src={calendarIcon} alt="calendar" className="w-5 h-5" />
            <span>{t('supportDateDisplay.showSupportDates')}</span>
            <span className="text-sm">{showSupportDates ? '▲' : '▼'}</span>
          </button>

          {showSupportDates && (
            <div className="flex items-center gap-3 mt-2 px-4 py-3 rounded-lg min-h-12" style={{ backgroundColor: 'var(--surface-grey-low-2)' }}>
              <div className="body-md-regular text-(--font-body-1)">
                {!supportDateInfo && <span className="text-(--font-body-2) animate-pulse">{t('calculating')}</span>}
                {supportDateInfo?.type === 'single' && <span>{t('supportDateDisplay.upcomingTransfer')}</span>}
                {supportDateInfo?.type === 'mixed' && (
                  <span>
                    {t('supportDateDisplay.supportCount', { count: supportDateInfo.count })}
                    <span className="mx-2">|</span>
                    {t('supportDateDisplay.firstSupportDate')}: {supportDateInfo.firstDate}
                    <span className="mx-2">|</span>
                    {t('supportDateDisplay.followingDates')}: {t('supportDateDisplay.onDayOfMonth', { day: supportDateInfo.followingDay })}
                  </span>
                )}
                {supportDateInfo?.type === 'uniform' && (
                  <span>
                    {t('supportDateDisplay.supportCountOnDay', { count: supportDateInfo.count, day: supportDateInfo.day })},{' '}
                    {t('supportDateDisplay.startingFrom', { date: supportDateInfo.startDate })}
                  </span>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="flex flex-row max-md:flex-col gap-6 max-md:gap-4 mt-6">
          <div className="flex-1">
            <label className="body-sm-regular text-(--font-heading) block mb-1.5">{t('paymentReceptionMethod')}</label>
            <select
              className="body-md-regular text-(--font-body-1) p-3 border border-(--border-main) rounded-lg w-full"
              value={paymentInfoId}
              onChange={(e) => setValue('paymentInfoId', Number(e.target.value))}
            >
              <option value={0} disabled>
                {t('selectPaymentMethod')}
              </option>
              {accountOptions.map((paymentInfo) => (
                <option key={paymentInfo.id} value={paymentInfo.id}>
                  {t('bank')} {paymentInfo.bankNumber}
                  {paymentInfo.bankName ? ` (${paymentInfo.bankName})` : ''} | {t('branch')} {paymentInfo.branchNumber} | {t('account')}{' '}
                  {paymentInfo.accountNumber}
                </option>
              ))}
            </select>
          </div>

          <div className="flex-1 min-w-0">
            <SupportAmountInput
              id="support-request-amount"
              label={t('supportAmount')}
              amount={amount}
              onAmountChange={(next) => setValue('amount', next)}
              placeholder={t('enterAmount')}
            />
            {supportRowFee > 0 && (
              <p className="body-sm-regular text-(--font-body-2) mt-1">
                {t('supportOverheadHint', { fee: formatCurrency(supportRowFee) })}
              </p>
            )}
          </div>
        </div>

        {/* SPR-10282: a supported without an account is added one here, without leaving the request.
            On its own row, under the list and the amount: inside the list's column it squeezed the amount field. */}
        {currentFund?.id && supported?.id && (
          <AddBankAccountInline
            key={supported.id}
            fundId={currentFund.id}
            supportedId={supported.id}
            hasAccounts={accountOptions.length > 0}
            onAdded={handleAccountAdded}
          />
        )}

        {/* SPR-10304: standing supports about to fall due are no longer taken off the available
            balance; the coordinator is told about them here instead, while deciding on the amount. */}
        <UpcomingStandingSupportsNotice upcoming={balance?.supportRequestsBalances?.upcomingStandingSupports} className="mt-6" />

        {/* Exceeds Balance/Eligible Amount Warning */}
        {(exceedsAvailableBalance || exceedsEligibleAmount) && (
          <div className="mt-6 flex flex-col gap-4">
            <div className="flex items-start justify-start gap-3 px-4 min-w-0">
              <img
                src={warningIcon}
                alt="warning"
                className="w-4 h-4 shrink-0 mt-0.5"
                style={{ filter: 'brightness(0) saturate(100%) invert(15%) sepia(60%) saturate(2000%) hue-rotate(320deg)' }}
              />
              <p className="body-lr-regular text-(--font-body-1) min-w-0 flex-1 break-words">
                {exceedsAvailableBalance && exceedsEligibleAmount && t('exceedsWarningBoth')}
                {exceedsAvailableBalance && !exceedsEligibleAmount && t('exceedsWarningBalanceOnly')}
                {!exceedsAvailableBalance && exceedsEligibleAmount && t('exceedsWarningEligibilityOnly')}
              </p>
            </div>
            <div
              className="flex items-start justify-start gap-4 px-5 py-2 rounded-full w-full min-w-0"
              style={{ backgroundColor: 'var(--surface-grey-low-2)' }}
            >
              <Checkbox checked={exceptionsCommitteeAcknowledged} onCheckedChange={(checked) => setValue('exceptionsCommitteeAcknowledged', checked)} />
              <p className="body-lr-regular text-(--font-body-1) min-w-0 flex-1 break-words">{t('exceptionsCommitteeAcknowledgement')}</p>
            </div>

            <div className="px-4">
              <label className="body-sm-regular text-(--font-heading) block mb-1.5">
                {t('exceedsBalanceCommentLabel')}
                <span className="text-red-500"> *</span>
              </label>
              <textarea
                className="body-md-regular text-(--font-body-1) p-3 border border-(--border-main) rounded-lg w-full resize-none"
                rows={3}
                placeholder={t('exceedsBalanceCommentPlaceholder')}
                value={exceedsBalanceComment || ''}
                onChange={(e) => setValue('exceedsBalanceComment', e.target.value)}
              />
            </div>
          </div>
        )}

        {/* HAT-1259: payment directed to someone other than the beneficiary. Sits at the end of
            the payment step because it is about where the money goes, after how much and when. */}
        <ThirdPartyPaymentSection supportedId={supported?.id ?? null} requestedForDate={requestedForDate} />
      </div>

      {/* Fund Balance Info Panel */}
      <InfoPanel title={t('fundAndSupportedBalanceData')} items={fundBalanceItems} className="w-[240px] max-md:w-full shrink-0" />
    </div>
  );
};

export default PaymentSetup;
